import { useEffect, useMemo, useRef, useState } from 'react';

import type { ChangeEvent, MouseEvent } from 'react';
import type { Meta, StoryFn } from '@storybook/react';

import { DataTable, type DataTableProps } from './DataTable';
import { MOCK_DATA } from './mockDataTableData';

import { Checkbox } from 'components';

interface MockDataProps {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  street: string;
  street_number: string;
  zipcode: string | null;
  city: string;
  country: string;
  country_code: string;
  trade: string;
  language: string;
  birthdate: string;
  quote: string;
  university: string;
  appname: string;
  appversion: string;
  website: string;
}

const formatWebsiteLabel = (website: string) => {
  try {
    const { origin } = new URL(website);
    return `${origin}`;
  } catch {
    return website;
  }
};

const BEGINNING_OF_TIME = 1970;
const PREVIEW_ROW_COUNT = 1000;

const codeString = `<DataTable<DataProps> 
          data={Array of data objects} 
          columns={columns} 
          stickyHeader={boolean}
          virtualize={
            { 
              rowHeight: {number}
              buffer: {number}
            }
          }
        />`;

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Organisms/DataTable',
  component: DataTable,
  argTypes: {
    data: { control: false },
    columns: { control: false },
    stickyHeader: { control: false },
    virtualize: { control: false },
    activeKeys: { control: false },
  },
  parameters: {
    inspectComponent: DataTable,
    codeString: codeString,
  },
} as Meta<DataTableProps<MockDataProps>>;

const Template: StoryFn<DataTableProps<MockDataProps>> = (args) => {
  const [rows, setRows] = useState<MockDataProps[]>(MOCK_DATA);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const lastSelectedRowId = useRef<string | null>(null);

  const checkColumnState = (): boolean | 'indeterminate' => {
    const everyRowActive = rows?.every((row) => selectedRows.includes(row.id));
    const someRowActive = rows?.some((row) => selectedRows.includes(row.id));
    if (everyRowActive) return true;
    else if (!everyRowActive && someRowActive) return 'indeterminate';
    else return false;
  };

  const headerCheckboxState = checkColumnState();

  const handleSelectRow = (event: ChangeEvent<HTMLInputElement>, rowId: string) => {
    const nativeEvent = event.nativeEvent as unknown as MouseEvent;

    if (nativeEvent.shiftKey && lastSelectedRowId.current) {
      const lastIndex = rows?.findIndex((row) => row.id === lastSelectedRowId.current) ?? -1;
      const currentIndex = rows?.findIndex((row) => row.id === rowId) ?? -1;
      const [start, end] = [lastIndex, currentIndex].sort((itemA, itemB) => itemA - itemB);
      const newSelectedRows = rows?.slice(start, end + 1).map((row) => row.id) ?? [];
      setSelectedRows((previous) => {
        const uniqueSelectedRows = new Set([...previous, ...newSelectedRows]);
        return Array.from(uniqueSelectedRows);
      });
      lastSelectedRowId.current = rowId;
    } else {
      setSelectedRows((previous) => {
        if (previous.includes(rowId)) {
          return previous.filter((id) => id !== rowId);
        } else {
          return [...previous, rowId];
        }
      });
      lastSelectedRowId.current = rowId;
    }
  };
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = headerCheckboxState === 'indeterminate';
    }
  }, [headerCheckboxState]);

  const columns = useMemo<DataTableProps<MockDataProps>['columns']>(
    () => [
      {
        key: 'id',
        label: () => (
          <Checkbox
            ref={headerCheckboxRef}
            checked={headerCheckboxState === true}
            onChange={() => {
              const shouldActivateAll = headerCheckboxState === true ? false : true;
              setSelectedRows(shouldActivateAll ? (rows?.map((row) => row.id) ?? []) : []);
            }}
          />
        ),
        cell: (row?: MockDataProps) => (
          <Checkbox
            checked={row ? Boolean(selectedRows.includes(row.id)) : false}
            onChange={(event) => {
              if (!row) return;
              handleSelectRow(event, row.id);
            }}
          />
        ),
        fixedWidth: '2rem',
      },
      {
        key: 'first_name',
        label: 'Fullname',
        cell: (row?: MockDataProps) => `${row?.first_name} ${row?.last_name}`,
      },
      {
        key: 'email',
        label: 'Email',
        cell: (row?: MockDataProps) => row?.email,
      },
      {
        key: 'street',
        label: 'Address',
        cell: (row?: MockDataProps) => (
          <span>
            {row?.street} {row?.street_number}
            <br />
            {row?.zipcode} {row?.city}
            <br />
            {row?.country}
          </span>
        ),
      },
      {
        key: 'birthdate',
        label: 'Birthdate/Age',
        cell: (row?: MockDataProps) => {
          const dateString = row?.birthdate?.split('-').reverse().join('-') ?? '';
          const birthDate = new Date(dateString);
          const ageDifMs = Date.now() - birthDate.getTime();
          const ageDate = new Date(ageDifMs);
          return `${row?.birthdate} (${Math.abs(ageDate.getUTCFullYear() - BEGINNING_OF_TIME)})`;
        },
      },
      {
        key: 'university',
        label: 'University',
        cell: (row?: MockDataProps) => row?.university,
      },
      {
        key: 'appname',
        label: 'App (version)',
        cell: (row?: MockDataProps) => `${row?.appname} (${row?.appversion})`,
      },
      {
        key: 'website',
        label: 'Website',
        cell: (row?: MockDataProps) => {
          const website = String(row?.website);
          return (
            <button
              onClick={() => alert(`You clicked on ${formatWebsiteLabel(website)}`)}
              style={{
                color: 'black',
                background: 'transparent',
                border: '1px solid gray',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                backgroundColor: '#f0f0f0',
              }}
            >
              website
            </button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerCheckboxState, rows, selectedRows]
  );

  return (
    <div style={{ position: 'absolute', inset: '1rem' }}>
      <input
        type="text"
        placeholder="Search..."
        style={{ marginBottom: '1rem', padding: '0.5rem', width: '100%' }}
        onChange={(event) => {
          const srcString = event.target.value;
          setRows(
            MOCK_DATA.slice(0, PREVIEW_ROW_COUNT).filter((item) => {
              return (
                item.university.includes(srcString) ||
                item.first_name.includes(srcString) ||
                item.last_name.includes(srcString)
              );
            })
          );
        }}
        disabled={selectedRows.length !== 0}
      />
      <span style={{ display: 'block', marginBottom: '0.5rem' }}>
        This is a preview of {PREVIEW_ROW_COUNT} rows. Filtered {rows?.length ?? 0} rows. Selected {selectedRows.length}{' '}
        rows.
      </span>

      <DataTable<MockDataProps>
        {...args}
        data={rows ?? []}
        columns={columns}
        activeKeys={selectedRows}
        onRowClick={(row) => console.log('row clicked:', row)}
      />
    </div>
  );
};

export const DataTableExample = Template.bind({});

DataTableExample.args = {
  stickyHeader: true,
  virtualize: { rowHeight: 90, buffer: 6 },
  noHover: false,
  noDataMessage: 'No records found',
};

const TemplateNotVirtualized: StoryFn<DataTableProps<MockDataProps>> = () => {
  const [rows, setRows] = useState<MockDataProps[]>(MOCK_DATA);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const lastSelectedRowId = useRef<string | null>(null);

  const checkColumnState = (): boolean | 'indeterminate' => {
    const everyRowActive = rows?.every((row) => selectedRows.includes(row.id));
    const someRowActive = rows?.some((row) => selectedRows.includes(row.id));
    if (everyRowActive) return true;
    else if (!everyRowActive && someRowActive) return 'indeterminate';
    else return false;
  };

  const headerCheckboxState = checkColumnState();

  const handleSelectRow = (event: ChangeEvent<HTMLInputElement>, rowId: string) => {
    const nativeEvent = event.nativeEvent as unknown as MouseEvent;

    if (nativeEvent.shiftKey && lastSelectedRowId.current) {
      const lastIndex = rows?.findIndex((row) => row.id === lastSelectedRowId.current) ?? -1;
      const currentIndex = rows?.findIndex((row) => row.id === rowId) ?? -1;
      const [start, end] = [lastIndex, currentIndex].sort((itemA, itemB) => itemA - itemB);
      const newSelectedRows = rows?.slice(start, end + 1).map((row) => row.id) ?? [];
      setSelectedRows((previous) => {
        const uniqueSelectedRows = new Set([...previous, ...newSelectedRows]);
        return Array.from(uniqueSelectedRows);
      });
      lastSelectedRowId.current = rowId;
    } else {
      setSelectedRows((previous) => {
        if (previous.includes(rowId)) {
          return previous.filter((id) => id !== rowId);
        } else {
          return [...previous, rowId];
        }
      });
      lastSelectedRowId.current = rowId;
    }
  };
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = headerCheckboxState === 'indeterminate';
    }
  }, [headerCheckboxState]);

  const columns = useMemo<DataTableProps<MockDataProps>['columns']>(
    () => [
      {
        key: 'id',
        label: () => (
          <Checkbox
            ref={headerCheckboxRef}
            checked={headerCheckboxState === true}
            onChange={() => {
              const shouldActivateAll = headerCheckboxState === true ? false : true;
              setSelectedRows(shouldActivateAll ? (rows?.map((row) => row.id) ?? []) : []);
            }}
          />
        ),
        cell: (row?: MockDataProps) => (
          <Checkbox
            checked={row ? Boolean(selectedRows.includes(row.id)) : false}
            onChange={(event) => {
              if (!row) return;
              handleSelectRow(event, row.id);
            }}
          />
        ),
        fixedWidth: '2rem',
      },
      {
        key: 'first_name',
        label: 'Fullname',
        cell: (row?: MockDataProps) => `${row?.first_name} ${row?.last_name}`,
        fixedWidth: '10rem',
      },
      {
        key: 'email',
        label: 'Email',
        cell: (row?: MockDataProps) => row?.email,
        fixedWidth: '14rem',
      },
      {
        key: 'street',
        label: 'Address',
        cell: (row?: MockDataProps) => (
          <span>
            {row?.street} {row?.street_number}
            <br />
            {row?.zipcode} {row?.city}
            <br />
            {row?.country}
          </span>
        ),
      },
      {
        key: 'birthdate',
        label: 'Birthdate/Age',
        cell: (row?: MockDataProps) => {
          const dateString = row?.birthdate?.split('-').reverse().join('-') ?? '';
          const birthDate = new Date(dateString);
          const ageDifMs = Date.now() - birthDate.getTime();
          const ageDate = new Date(ageDifMs);
          return `${row?.birthdate} (${Math.abs(ageDate.getUTCFullYear() - BEGINNING_OF_TIME)})`;
        },
        fixedWidth: '12rem',
      },
      {
        key: 'university',
        label: 'University',
        fixedWidth: '20rem',
      },
      {
        key: 'appname',
        label: 'App (version)',
        cell: (row?: MockDataProps) => `${row?.appname} (${row?.appversion})`,
        fixedWidth: '12rem',
      },
      {
        key: 'website',
        label: 'Website',
        cell: (row?: MockDataProps) => {
          const website = String(row?.website);
          return (
            <button
              onClick={() => alert(`You clicked on ${formatWebsiteLabel(website)}`)}
              style={{
                color: 'black',
                background: 'transparent',
                border: '1px solid gray',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                backgroundColor: '#f0f0f0',
              }}
            >
              website
            </button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerCheckboxState, rows, selectedRows]
  );

  return (
    <div style={{ position: 'absolute', inset: '1rem' }}>
      <input
        type="text"
        placeholder="Search..."
        style={{ marginBottom: '1rem', padding: '0.5rem', width: '100%' }}
        onChange={(event) => {
          const srcString = event.target.value;
          setRows(
            MOCK_DATA.slice(0, PREVIEW_ROW_COUNT).filter((item) => {
              return (
                item.university.includes(srcString) ||
                item.first_name.includes(srcString) ||
                item.last_name.includes(srcString)
              );
            })
          );
        }}
        disabled={selectedRows.length !== 0}
      />
      <span style={{ display: 'block', marginBottom: '0.5rem' }}>
        This is a preview of {PREVIEW_ROW_COUNT} rows. Filtered {rows?.length ?? 0} rows. Selected {selectedRows.length}{' '}
        rows.
      </span>

      <DataTable<MockDataProps>
        data={rows ?? []}
        columns={columns}
        stickyHeader={false}
        noDataMessage="No records found"
        onRowClick={(row) => console.log('row clicked:', row)}
      />
    </div>
  );
};

export const DataTableNotVirtualizedExample = TemplateNotVirtualized.bind({});
